import React from 'react';
import { CompositeDecorator } from 'draft-js';
import { List } from 'immutable';
import uniq from 'lodash/uniq';

const SpanComponent = props => {
  return <span>{props.children}</span>;
};

const HighlightComponent = (props, matches) => {
  return <em>{props.children}</em>;
};

const UnderlineComponent = className => {
  return props => <strong className={className}>{props.children}</strong>;
};

export const getDecorators = (synonyms, entities, tags, domain, metadata) => {
  const decorators = [
    {
      strategy: (contentBlock, callback) =>
        _blueUnderlineStrategy(
          contentBlock,
          callback,
          synonyms,
          tags,
          domain,
          metadata
        ),
      component: UnderlineComponent('blue'),
    },
    {
      strategy: (contentBlock, callback) =>
        _blueDashedUnderlineStrategy(
          contentBlock,
          callback,
          synonyms,
          tags,
          domain,
          metadata
        ),
      component: UnderlineComponent('blue-dashed'),
    },
    {
      strategy: (contentBlock, callback) =>
        _greenUnderlineStrategy(
          contentBlock,
          callback,
          entities,
          tags,
          domain,
          metadata
        ),
      component: UnderlineComponent('green'),
    },
    {
      strategy: (contentBlock, callback) =>
        _greenDashedUnderlineStrategy(
          contentBlock,
          callback,
          entities,
          tags,
          domain,
          metadata
        ),
      component: UnderlineComponent('green-dashed'),
    },
    // {
    //   strategy: (contentBlock, callback) =>
    //     _underlineStrategy(contentBlock, callback, synonyms, tags, domain),
    //   component: UnderlineComponent(''),
    // },
    new CompositeDecorator([
      {
        strategy: (contentBlock, callback) =>
          _highlightStrategy(contentBlock, callback, synonyms, tags, domain),
        component: HighlightComponent,
      },
    ]),
  ];

  const decorator = new CompoundDecorator(decorators);
  return decorator;
};

function _highlightStrategy(contentBlock, callback, synonyms, tags, domain) {
  const text = contentBlock.getText();
  const wordsToStyle = _getWordsToHighlight({ tags });
  _searchText(text, wordsToStyle, callback);
}

// function _underlineStrategy(contentBlock, callback, synonyms, tags, domain) {
//   const text = contentBlock.getText();
//   const wordsToStyle = _getWordsToUnderline({ synonyms, domain }, true);
//   _searchText(text, wordsToStyle, callback);
// }

function _blueUnderlineStrategy(
  contentBlock,
  callback,
  synonyms,
  tags,
  domain,
  metadata
) {
  const text = contentBlock.getText();
  const wordsToStyle = _getWordsToUnderline({ synonyms, metadata }, true);
  if (wordsToStyle) _searchText(text, wordsToStyle, callback);
}
function _blueDashedUnderlineStrategy(
  contentBlock,
  callback,
  synonyms,
  tags,
  domain,
  metadata
) {
  const text = contentBlock.getText();
  const wordsToStyle = _getWordsToUnderline({ synonyms, metadata }, false);
  if (wordsToStyle) _searchText(text, wordsToStyle, callback);
}
function _greenUnderlineStrategy(
  contentBlock,
  callback,
  entities,
  tags,
  domain,
  metadata
) {
  const text = contentBlock.getText();
  const wordsToStyle = _getEntitiesToUnderline({ entities, metadata }, true);
  if (wordsToStyle) _searchText(text, wordsToStyle, callback);
}
function _greenDashedUnderlineStrategy(
  contentBlock,
  callback,
  entities,
  tags,
  domain,
  metadata
) {
  const text = contentBlock.getText();
  const wordsToStyle = _getEntitiesToUnderline({ entities, metadata }, false);
  if (wordsToStyle) _searchText(text, wordsToStyle, callback);
}

class CompoundDecorator {
  constructor(decorators = []) {
    this.decorators = decorators.map(decorator => {
      return decorator.strategy && decorator.component
        ? new CompositeDecorator([decorator])
        : decorator;
    });
  }

  getDecorations(block) {
    const emptyTuples = Array(block.getText().length).fill(
      Array(this.decorators.length).fill(null)
    );

    const decorations = this.decorators.reduce((tuples, decorator, index) => {
      const blockDecorations = decorator.getDecorations(block);
      return tuples.map((tuple, tupleIndex) => {
        return [
          ...tuple.slice(0, index),
          blockDecorations.get(tupleIndex),
          ...tuple.slice(index + 1),
        ];
      });
    }, emptyTuples);
    return List(decorations.map(JSON.stringify));
  }

  getComponentForKey(key) {
    const tuple = JSON.parse(key);
    return props => {
      const { decoratorProps, ...compositionProps } = props;
      const Composed = tuple.reduce((Composition, decoration, index) => {
        if (decoration !== null) {
          const decorator = this.decorators[index];
          const Component = decorator.getComponentForKey(decoration);
          const componentProps = {
            ...compositionProps,
            ...decoratorProps[index],
          };
          return () => (
            <Component {...componentProps}>
              <Composition {...compositionProps} />
            </Component>
          );
        }
        return Composition;
      }, SpanComponent);
      return <Composed>{props.children}</Composed>;
    };
  }

  getPropsForKey(key) {
    const tuple = JSON.parse(key);
    return {
      decoratorProps: tuple.map((decoration, index) => {
        const decorator = this.decorators[index];
        return decoration !== null ? decorator.getPropsForKey(decoration) : {};
      }),
    };
  }
}

function _getWordsToUnderline(
  { synonyms, metadata = { entity_meta: {}, cluster_meta: {} } },
  shouldBeActive
) {
  const STYLE_THESE_WORDS = [];
  if (!synonyms || !metadata.cluster_meta) return;

  const meta = metadata.cluster_meta;
  return Object.keys(meta)
    .map(key => key)
    .filter(
      key =>
        synonyms[meta[key]] && synonyms[meta[key]].active === shouldBeActive
    );
}

function _getEntitiesToUnderline(
  { entities = {}, metadata = { entity_meta: {}, cluster_meta: {} } },
  shouldBeActive
) {
  const STYLE_THESE_WORDS = [];
  if (
    Object.keys(entities).length === 0 ||
    Object.keys(metadata.entity_meta).length === 0
  )
    return;
  const meta = metadata.entity_meta;
  return Object.keys(meta)
    .map(key => key)
    .filter(key => entities[meta[key]].active === shouldBeActive);
}

function _getWordsToHighlight({ tags }) {
  tags = uniq(tags);
  return tags;
}

function _searchText(text, wordsToStyle, callback) {
  wordsToStyle.map(word => {
    const strippedWord = word.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
    if (strippedWord.length < 1) return;
    const regex = new RegExp(strippedWord, 'gi');
    let matchArr;
    let start;
    while ((matchArr = regex.exec(text)) !== null) {
      start = matchArr.index;
      callback(start, start + matchArr[0].length);
    }
  });
}
